/**
 * v3Shared.js — Shared design tokens + helpers for the V3 task surface family.
 *
 * Used by CreateTaskModalV3, EditTaskModalV3, TaskRowV3, TaskTableV3,
 * TaskTileV3 (Kanban) and AIChatTaskCardV3.
 *
 * V3 design language:
 *   • Priority drives the dominant accent (left-edge bar + button gradient)
 *   • Work area drives the secondary accent (top strip + token chip color)
 *   • Status uses the semantic state map (todo / inProgress / done / blocked)
 *   • Surfaces are quiet (var(--orka-surface-base)); raised tones for hovers
 *   • All chips/labels use natural case — never ALL CAPS — and size to text.
 */

export const V3_PRIORITY_COLORS = {
  // P1 = ember orange ∙ P2 = burnt amber ∙ P3 = mid blue ∙ P4 = slate ∙ P5 = stone
  // -1 = personal (lavender)
  1: "#ea580c",
  2: "#d97706",
  3: "#2563eb",
  4: "#475569",
  5: "#94a3b8",
  "-1": "#7c3aed",
};

export const V3_PRIORITY_LABEL = {
  1: "P1 · Critical",
  2: "P2 · High",
  3: "P3 · Medium",
  4: "P4 · Low",
  5: "P5 · Nice-to-have",
  "-1": "Personal",
};

export const V3_PRIORITY_SHORT = {
  1: "P1",
  2: "P2",
  3: "P3",
  4: "P4",
  5: "P5",
  "-1": "Pers",
};

export const V3_EFFORT_COLORS = {
  1: "#10b981", // 15m  green
  2: "#22c55e", // 30m  green-500
  3: "#3b82f6", // 60m  blue
  5: "#8b5cf6", // 180m violet
  8: "#a855f7", // 360m purple
  13: "#ec4899", // 600m pink
};

export const V3_EFFORT_LABEL = {
  1: "15 min",
  2: "30 min",
  3: "60 min",
  5: "3 hr",
  8: "6 hr",
  13: "10 hr",
};

export const V3_EFFORT_MINUTES = { 1: 15, 2: 30, 3: 60, 5: 180, 8: 360, 13: 600 };

export const V3_STATUS_COLORS = {
  todo: "#64748b",
  inProgress: "#2563eb",
  done: "#10b981",
  blocked: "#dc2626",
};
export const V3_STATUS_LABEL = {
  todo: "To Do",
  inProgress: "In Progress",
  done: "Done",
  blocked: "Blocked",
};
export const V3_STATUS_KEYS = ["todo", "inProgress", "done", "blocked"];

// Legacy task_status string ↔ canonical task_status_key.
export const STATUS_KEY_TO_LEGACY = {
  todo: "To Do",
  inProgress: "In Progress",
  done: "Done",
  blocked: "Blocked",
};
export const LEGACY_TO_STATUS_KEY = {
  "To Do": "todo",
  "In Progress": "inProgress",
  Done: "done",
  Blocked: "blocked",
};

export function statusKeyOf(task) {
  if (task?.task_status_key) return task.task_status_key;
  return LEGACY_TO_STATUS_KEY[task?.task_status] || "todo";
}

export function priorityColorOf(task) {
  const p = task?.task_priority ?? 3;
  return V3_PRIORITY_COLORS[p] || V3_PRIORITY_COLORS[3];
}

export function workAreaColorOf(task, workAreas = []) {
  const wa = workAreas.find((w) => w.work_area_id === task?.work_area_id);
  return wa?.color || "#64748b";
}

export function workAreaLabelOf(task, workAreas = []) {
  const wa = workAreas.find((w) => w.work_area_id === task?.work_area_id);
  return wa?.label || wa?.name || task?.work_area_id || "—";
}

export function effortMinutesOf(task) {
  if (task?.task_estimated_minutes != null) return Number(task.task_estimated_minutes);
  return V3_EFFORT_MINUTES[task?.task_estimated_effort] || 60;
}

export function addMinutesToTime(hhmm, mins) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const total = h * 60 + m + mins;
  const eh = Math.floor((total % (24 * 60)) / 60);
  const em = total % 60;
  return `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
}

export function formatTimeRange(task) {
  const s = task?.task_start_time || task?.task_local_start_time;
  const e = task?.task_end_time || task?.task_local_end_time;
  if (!s && !e) return "";
  return `${s || "--:--"} – ${e || "--:--"}`;
}

export function formatDate(task) {
  const d = task?.task_delivery_date || task?.task_local_date;
  return d || "";
}

/**
 * V3 surface CSS — injected once and shared across all V3 task surface
 * components. Class prefix `.v3sf-…` ("V3 Surface Family") to avoid
 * colliding with any existing v3 task-card scope.
 */
export const V3_SURFACE_FAMILY_CSS = `
.v3sf-scope, .v3sf-scope *{box-sizing:border-box}
.v3sf-scope{
  --v3sf-bg: var(--orka-surface-base, #ffffff);
  --v3sf-raised: var(--orka-surface-raised, #f8fafc);
  --v3sf-subtle: var(--orka-surface-subtle, #f1f5f9);
  --v3sf-border: var(--orka-border-default, rgba(15,23,42,.12));
  --v3sf-border-strong: var(--orka-border-strong, rgba(15,23,42,.22));
  --v3sf-text: var(--orka-text-primary, #0f172a);
  --v3sf-text-2: var(--orka-text-secondary, #334155);
  --v3sf-text-3: var(--orka-text-tertiary, #64748b);
  color: var(--v3sf-text);
}
.v3sf-chip{
  display:inline-flex;align-items:center;gap:6px;height:24px;padding:0 10px;
  border-radius:999px;font:900 11.5px/1 Inter,system-ui,sans-serif;letter-spacing:.005em;
  border:1px solid color-mix(in srgb,var(--token-color,#64748b) 28%,var(--v3sf-border));
  background:color-mix(in srgb,var(--token-color,#64748b) 9%,var(--v3sf-raised));
  color:var(--token-color,var(--v3sf-text-2));white-space:nowrap;text-transform:none;
}
.v3sf-chip .v3sf-dot{
  width:7px;height:7px;border-radius:999px;background:var(--token-color,#64748b);
  box-shadow:0 0 0 3px color-mix(in srgb,var(--token-color,#64748b) 16%,transparent);flex:none;
}
.v3sf-chip select{
  appearance:none;border:0;outline:0;background:transparent;color:inherit;
  font:inherit;cursor:pointer;padding-right:14px;
  width:auto;min-width:0;max-width:none;
  background-image:linear-gradient(45deg,transparent 50%,currentColor 50%),linear-gradient(135deg,currentColor 50%,transparent 50%);
  background-position:calc(100% - 7px) center, calc(100% - 3px) center;
  background-size:4px 4px,4px 4px;background-repeat:no-repeat;
  text-transform:none;
}
.v3sf-chip select option{color:#0f172a;background:#fff;text-transform:none}
.v3sf-status-pill{
  display:inline-flex;align-items:center;gap:8px;height:30px;padding:0 12px;
  border-radius:999px;font:900 12px/1 Inter,system-ui,sans-serif;color:#fff;
  background:linear-gradient(180deg,var(--status-color,#64748b),color-mix(in srgb,var(--status-color,#64748b) 72%,#000));
  box-shadow:0 6px 14px color-mix(in srgb,var(--status-color,#64748b) 28%,transparent);
  border:0;cursor:pointer;text-transform:none;
}
.v3sf-status-pill .v3sf-status-dot{width:8px;height:8px;border-radius:999px;background:#fff;opacity:.92}
.v3sf-priority-rail{
  position:absolute;left:0;top:0;bottom:0;width:4px;border-radius:4px 0 0 4px;
  background:linear-gradient(180deg,var(--priority-color,#64748b),color-mix(in srgb,var(--priority-color,#64748b) 70%,#000));
  box-shadow:0 0 12px color-mix(in srgb,var(--priority-color,#64748b) 24%,transparent);
}
.v3sf-workarea-strip{
  position:absolute;top:0;left:4px;right:0;height:3px;
  background:linear-gradient(90deg,var(--workarea-color,#64748b),color-mix(in srgb,var(--workarea-color,#64748b) 50%,transparent));
  opacity:.72;
}
.v3sf-meta{display:inline-flex;align-items:center;gap:5px;color:var(--v3sf-text-3);font:900 11.5px/1 Inter,system-ui,sans-serif}
`;

let v3sfInjected = false;
export function injectV3SurfaceCSS() {
  if (v3sfInjected || typeof document === "undefined") return;
  const el = document.createElement("style");
  el.setAttribute("data-v3sf", "1");
  el.textContent = V3_SURFACE_FAMILY_CSS;
  document.head.appendChild(el);
  v3sfInjected = true;
}
